import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import DocumentIcon from "@components/icons/DocumentIcon";
import ClockRotateIcon from "@components/icons/ClockRotateIcon";
import ExpandInfoPanelButton from "@components/shared/ExpandInfoPanelButton";
import { useAppContext } from "@context/AppContext";

export type ReportingSection = "templates" | "history";

type ReportingWorkspaceProps = {
  activeSection: ReportingSection;
  children: ReactNode;
  infoPanel?: ReactNode;
  contentClassName?: string;
};

/**
 * The persistent General Reports workspace. List, run and read-only pages all
 * keep the same section navigation and information-panel proportions; only
 * the centre content changes with the task at hand.
 */
const ReportingWorkspace: React.FunctionComponent<ReportingWorkspaceProps> = ({
  activeSection,
  children,
  infoPanel,
  contentClassName,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isExpandInfoPanel } = useAppContext();

  const sections: Array<{
    id: ReportingSection;
    label: string;
    icon: ReactNode;
    href: string;
  }> = [
    {
      id: "templates",
      label: t("reporting.templates_title"),
      icon: <DocumentIcon />,
      href: "/polestar/reports/adhoc#0",
    },
    {
      id: "history",
      label: t("reporting.history_title"),
      icon: <ClockRotateIcon />,
      href: "/polestar/reports/adhoc#1",
    },
  ];

  return (
    <div className="flex min-w-0 grow gap-4">
      <aside className="w-1/6 min-w-min text-base">
        <nav aria-label={t("reporting.hub_title") as string}>
          <ul className="divide-y border bg-white">
            {sections.map(section => {
              const isActive = section.id === activeSection;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    aria-current={isActive ? "page" : undefined}
                    className={twMerge(
                      "flex w-full items-center justify-start gap-3 border-r-4 p-4 text-left text-primary-3",
                      isActive
                        ? "border-r-primary-2 bg-blue-50 font-semibold"
                        : "border-r-transparent font-extralight opacity-70 hover:bg-blue-50",
                    )}
                    onClick={() => navigate(section.href)}
                  >
                    {section.icon}
                    {section.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      <div
        className={twMerge(
          "relative flex grow flex-col border bg-white",
          infoPanel && isExpandInfoPanel ? "w-3/5" : "w-4/5",
          contentClassName,
        )}
      >
        {children}
        {infoPanel && !isExpandInfoPanel && <ExpandInfoPanelButton />}
      </div>

      {infoPanel && isExpandInfoPanel && (
        <aside className="flex w-1/5">{infoPanel}</aside>
      )}
    </div>
  );
};

export default ReportingWorkspace;
